import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useServices } from '@/shared/hooks/use-services';
import { useStoreSlug } from '@/shared/hooks/use-store-slug';
import type { CartItem } from '@/shared/lib/db/schemas/cart.schema';
import type { GetStoreItemsResponse, StoreItem } from '@/modules/stores/types/get-store-items';
import {
  calculateQuantityPromotion,
  cheapestEligibleLineKey,
  isQuantityPromotionActive,
  quantityPromotionCta,
  quantityPromotionHint,
  quantityPromotionLineHint,
  quantityPromotionScopeLabel,
  type QuantityPromotionLine,
  type QuantityPromotionRule,
} from '@/shared/utils/quantity-promotion';
import {
  buildCategoryNameById,
  participatingCategoryNames,
} from '@/modules/stores/utils/quantity-promotion-scope';
import {
  calculateWholesale,
  wholesaleHint,
  wholesalePackViolations,
  type WholesaleRule,
} from '@/shared/utils/wholesale-pricing';

/** Canal do app do cliente na promoção por quantidade (PRAT-184). */
export const QUANTITY_PROMOTION_CHANNEL = 'app' as const;

/** Mapa produto (`item_id`) → categoria do cardápio, para o fallback. */
export type CategoryByItemId = Record<string, string | null | undefined>;

/**
 * Linhas da promoção a partir do carrinho: o preço unitário é o total da linha
 * (De/Por + complementos + extras de combo, como o app já soma) dividido pela
 * quantidade. Resgate do Clube não tem preço, logo não participa.
 *
 * Linha gravada sem `category_id` (carrinho no Dexie de antes deste campo
 * existir) resolve a categoria pelo catálogo em memória (`categoryByItemId`).
 */
export function cartItemsToPromotionLines(
  items: CartItem[] | undefined,
  categoryByItemId?: CategoryByItemId
): QuantityPromotionLine[] {
  return (items ?? []).map(item => {
    const quantity = Number(item.quantity) || 0;
    const total = Number(item.total_price) || 0;
    const categoryId = item.category_id || categoryByItemId?.[item.item_id] || null;
    return {
      key: item.id,
      item_id: item.item_id,
      category_id: categoryId,
      quantity,
      unit_price: item.is_reward || quantity <= 0 ? 0 : total / quantity,
    };
  });
}

/** Índice `item_id` → `category_id` a partir da resposta do cardápio. */
export function buildCategoryByItemId(
  catalog: Pick<GetStoreItemsResponse, 'items'> | undefined | null
): CategoryByItemId {
  const index: CategoryByItemId = {};
  if (!catalog?.items) return index;
  for (const group of Object.values(catalog.items)) {
    for (const product of (group ?? []) as StoreItem[]) {
      if (product?.id && product.category_id && !index[product.id]) {
        index[product.id] = product.category_id;
      }
    }
  }
  return index;
}

/**
 * Prévia da promoção por quantidade para o carrinho (canal `app`). A API
 * recalcula no pedido — aqui é só exibição.
 */
export function useCartQuantityPromotion(
  items: CartItem[] | undefined,
  rule: QuantityPromotionRule | null | undefined,
  categoryByItemId?: CategoryByItemId,
  /** Nomes das categorias participantes (do catálogo), para o "Vale para:". */
  categoryNames: string[] = [],
  /** PRAT-187: atacado por faixa; por linha vale o maior desconto (como na API). */
  wholesaleRule: WholesaleRule | null = null
) {
  return useMemo(() => {
    const lines = cartItemsToPromotionLines(items, categoryByItemId);
    const result = calculateQuantityPromotion(rule ?? null, lines, QUANTITY_PROMOTION_CHANNEL);
    const wholesale = calculateWholesale(wholesaleRule, lines);
    let wholesaleAmount = 0;
    let promoAmount = 0;
    for (const key of new Set([...Object.keys(result.lines), ...Object.keys(wholesale.lines)])) {
      const q = result.lines[key] ?? 0;
      const w = wholesale.lines[key] ?? 0;
      if (w > q) wholesaleAmount += w;
      else promoAmount += q;
    }
    const amount = Math.round((wholesaleAmount + promoAmount) * 100) / 100;
    const active = isQuantityPromotionActive(rule, QUANTITY_PROMOTION_CHANNEL);
    const eligibleKeys = new Set(result.eligible_keys);
    const cta = quantityPromotionCta(result, rule);
    const cheapestKey = cta ? cheapestEligibleLineKey(result, lines) : null;
    const cheapestLine = cheapestKey
      ? ((items ?? []).find(item => item.id === cheapestKey) ?? null)
      : null;
    return {
      rule: rule ?? null,
      /** A promoção vale no app agora (ativa, percentual > 0, canal `app`)? */
      active,
      result,
      amount,
      /** Parte do desconto que é preço de atacado (PRAT-187). */
      wholesaleAmount: Math.round(wholesaleAmount * 100) / 100,
      /** Rótulo do desconto no resumo. */
      discountLabel:
        wholesaleAmount > 0 && promoAmount > 0
          ? 'Atacado e promoção'
          : wholesaleAmount > 0
            ? 'Preço de atacado'
            : 'Promoção por quantidade',
      /** "Gelo de sabores: faltam 30 un para R$ 1,80/un". */
      wholesaleHint: wholesaleHint(wholesale),
      /** Família só em caixa fechada fora do múltiplo: o pedido é recusado. */
      packViolations: wholesalePackViolations(wholesaleRule, lines),
      hint: quantityPromotionHint(result, rule),
      /** Aviso para mostrar junto de cada linha participante. */
      lineHint: quantityPromotionLineHint(result, rule),
      /** A linha (id do item do carrinho) participa da promoção? */
      isLineEligible: (key: string) => eligibleKeys.has(key),
      /**
       * Linha fora da promoção (categoria de fora, excluída, resgate do Clube)
       * enquanto a promoção está ativa: merece o aviso "Não entra na promoção".
       */
      isLineOutside: (key: string) => active && !eligibleKeys.has(key),
      /** "Vale para: Hambúrguers" — null sem regra ou sem nome resolvido. */
      scope: active ? quantityPromotionScopeLabel(rule, categoryNames) : null,
      /** Banner de destaque ("Falta 1 item...") — null quando não faltam 1 ou 2. */
      cta,
      /** Linha participante mais barata: é a que o banner oferece repetir. */
      cheapestLine,
    };
  }, [items, rule, categoryByItemId, categoryNames, wholesaleRule]);
}

/**
 * Regra da promoção por quantidade da loja atual e o índice de categorias do
 * cardápio (`GET /items` → `quantity_promotion` e `category_id` por item;
 * mesma query do catálogo, já em cache).
 */
export function useStoreQuantityPromotion(): {
  rule: QuantityPromotionRule | null;
  categoryByItemId: CategoryByItemId;
  /** Nomes das categorias que participam ("Hambúrguers"), pelo catálogo. */
  categoryNames: string[];
  /** PRAT-187: famílias de atacado da loja. */
  wholesaleRule: WholesaleRule | null;
} {
  const { getStoreItems } = useServices();
  const { slug } = useStoreSlug();

  const storeItemsQuery = useQuery({
    queryFn: () => getStoreItems.execute({ slug: slug! }),
    queryKey: ['store-items', slug],
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const categoryByItemId = useMemo(
    () => buildCategoryByItemId(storeItemsQuery.data),
    [storeItemsQuery.data]
  );

  const rule = storeItemsQuery.data?.quantity_promotion ?? null;

  const categoryNames = useMemo(
    () => participatingCategoryNames(rule, buildCategoryNameById(storeItemsQuery.data)),
    [rule, storeItemsQuery.data]
  );

  return {
    rule,
    categoryByItemId,
    categoryNames,
    wholesaleRule: storeItemsQuery.data?.wholesale_pricing ?? null,
  };
}

/** Só a regra da promoção por quantidade da loja atual. */
export function useStoreQuantityPromotionRule(): QuantityPromotionRule | null {
  return useStoreQuantityPromotion().rule;
}
